import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";

import { PrivateShipmentChip } from "components/PrivateShipmentChip";
import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { ShipmentCustomNameText } from "components/Texts/ShipmentCustomNameText";
import { ShipmentEquipmentTypeText } from "components/Texts/ShipmentEquipmentTypeText";
import { ShipmentFootageText } from "components/Texts/ShipmentFootageText";
import { ShipmentIDText } from "components/Texts/ShipmentIDText";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import { ShipmentPriceText } from "components/Texts/ShipmentPriceText";
import {
  convertSubdivisionCodeToSimplifiedSubdivisionCode,
  formatText,
  getCommdityNameForCondensedShipmentShipment,
  getServiceTypeDisplayLabel,
} from "utils";

import { MultiShipmentProjectLoadsAvailableLoadsLabel } from "../MultiShipmentProjectLoadsAvailableLoadsLabel";
import { QuoteCountChip } from "../QuoteCountChip";
import { SharedShipmentChip } from "../SharedShipmentChip";
import { ShipmentAuthorChip } from "./components/ShipmentAuthorChip";

const MIDDLE_MOUSE_BUTTON = 1;

interface Props {
  shipment: ShipperCondensedShipment;
}

export function ShipmentPreview(props: Props) {
  const { shipment } = props;
  const navigate = useNavigate();
  const shipmentInfo = shipment.info;
  const shipmentPath = `/shipments/${shipmentInfo.id}`;
  const firstPointLocation = shipment?.first_point?.point_location;
  const lastPointLocation = shipment?.last_point?.point_location;

  // Links inside the card, such as the quote count, navigate somewhere else.
  const eventTargetIsNestedLink = (event: React.MouseEvent<HTMLElement>) =>
    (event.target as HTMLElement).closest("a") != null;

  // The overlay link takes no pointer events, so every click the browser would
  // have handled on it has to be reproduced here.
  const openShipmentInNewTab = () => {
    // `window.open` does not imply `noopener`, and passing it in the feature
    // string makes the browser open a popup window rather than a tab, so the
    // opener is cleared on the handle instead.
    const newTab = window.open(shipmentPath, "_blank");
    if (newTab != null) {
      newTab.opener = null;
    }
  };

  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    // A drag that ended in a text selection is not a click on the card.
    if (window.getSelection()?.toString()) {
      return;
    }
    if (eventTargetIsNestedLink(event)) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey) {
      openShipmentInNewTab();
      return;
    }
    navigate(shipmentPath);
  };

  // Middle-click autoscroll begins on mousedown and swallows the mouseup, so
  // `auxclick` never arrives unless that default is suppressed first.
  const handleCardMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.button === MIDDLE_MOUSE_BUTTON &&
      !eventTargetIsNestedLink(event)
    ) {
      event.preventDefault();
    }
  };

  const handleCardAuxClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.button !== MIDDLE_MOUSE_BUTTON ||
      eventTargetIsNestedLink(event)
    ) {
      return;
    }
    openShipmentInNewTab();
  };

  return (
    <Box
      display="flex"
      px="2rem"
      py="1rem"
      onClick={handleCardClick}
      onMouseDown={handleCardMouseDown}
      onAuxClick={handleCardAuxClick}
      sx={{
        position: "relative",
        "&:hover": {
          bgcolor: "#f8f8f8",
          cursor: "pointer",
        },
      }}
    >
      {/*
        A real link so the card is reachable by keyboard and announced as a link, but with
        pointer events off: an overlay that swallowed them would take the card's text out of
        selection and its chips out of tooltip hover. Mouse clicks go to handleCardClick.
      */}
      <Box
        component={Link}
        to={shipmentPath}
        aria-label={`View shipment ${shipmentInfo.id}`}
        sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
          <Stack spacing={1}>
            <ShipmentIDText shipment={shipment} />
            {shipment?.custom_name != null && (
              <ShipmentCustomNameText data={shipment.custom_name} />
            )}
            <MultiShipmentProjectLoadsAvailableLoadsLabel shipment={shipment} />
            <Typography sx={{ textTransform: "capitalize" }}>
              {getServiceTypeDisplayLabel(shipmentInfo.service)}
            </Typography>
            <ShipmentEquipmentTypeText data={shipment?.equipments} />
          </Stack>
        </Grid>
        <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
          <Box ml="2rem" flex={1} alignSelf="center">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={{ textTransform: "capitalize" }} variant="body1">
                {formatText(
                  getCommdityNameForCondensedShipmentShipment(shipmentInfo),
                )}
              </Typography>
              {(shipment?.access_type === "ADMIN" ||
                shipment?.access_type === "SHAREE_READ_AND_WRITE") && (
                <ShipmentAuthorChip shipment={shipment} />
              )}
            </Stack>

            <Stack my="0.5rem" direction="row" alignItems="center">
              <ShipmentLocationText
                data={`${firstPointLocation.city}, ${firstPointLocation.province ? convertSubdivisionCodeToSimplifiedSubdivisionCode(firstPointLocation.province) : ""}`}
                icon={
                  <TripOriginIcon
                    sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                  />
                }
              />
              <Box flex={1} mx="1rem">
                <Divider />
              </Box>
              <ShipmentLocationText
                data={`${lastPointLocation.city}, ${lastPointLocation.province ? convertSubdivisionCodeToSimplifiedSubdivisionCode(lastPointLocation.province) : ""}`}
                icon={
                  <FmdGoodIcon
                    sx={{ fontSize: "0.9rem", marginRight: "5px" }}
                  />
                }
              />
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={4}
            mx="2rem"
            justifySelf="flex-end"
            alignSelf="center"
          >
            <ShipmentFootageText data={shipment?.info?.footage} />
            <ShipmentPriceText
              price={shipment?.info?.preferred_price}
              currency={shipment?.info?.currency}
            />
          </Stack>
          <Stack
            direction="row"
            mx="2rem"
            mt="0.5rem"
            alignItems="center"
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            <ShipmentStatusBadge
              status={shipment?.shipment_status}
              size="medium"
            />
            <Box
              component={Link}
              to={`${shipmentPath}?focus=quotes`}
              sx={{ textDecoration: "none" }}
            >
              <QuoteCountChip
                quoteCount={
                  shipment?.condensed_quotes.filter(
                    (q) => q.hidden_at == null && !q.carrier_is_blocked,
                  ).length
                }
              />
            </Box>
            <PrivateShipmentChip
              isPrivate={shipment?.is_private}
              isProject={shipment?.info?.type === "MULTI_SHIPMENT_PROJECT"}
              size="medium"
              color="info"
            />
            <SharedShipmentChip shipment={shipment} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
